import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Button,
    Input,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Text,
} from '@fluentui/react-components';
import {
    ArrowClockwise24Regular,
    ChevronRight16Regular,
    Dismiss20Regular,
    Search20Regular,
} from '@fluentui/react-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import * as ComponentFunctions from './ProtocolConsumptionSyncProcessLog.Functions';
import { ProtocolConsumptionSyncProcessLog } from '../../../../models/Entities/Protocols/ProtocolConsumptionSyncProcessLog';
import { SyncProcessLogSeverityEnum } from '../../../../models/Enums/SyncProcessLogSeverityEnum';
import { SyncProcessLogSubjectTypeEnum } from '../../../../models/Enums/SyncProcessLogSubjectTypeEnum';
import { useProtocolConsumptionSyncProcessLogStyles } from './ProtocolConsumptionSyncProcessLog.Styles';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { ISyncProcessLogsFilters, hasActiveSyncProcessLogsFilters } from '../../../../models/Filters/SyncProcessLogsFilters';
import { SYNC_PROCESS_LOGS_DEFAULT_PAGE_SIZE } from '../../../../redux/middleware/ProtocolConsumptionSyncProcessMiddleware';
import { PaginationControlComponent } from '../../../../commons/component/PaginationControl/PaginationControl.Component';
import { getSyncProcessLogLevel, isSeverityBelowThreshold } from '../../../../models/Entities/Protocols/SyncProcessLogLevel';

/** Cuánto se espera después de la última tecla antes de volver a consultar. */
const TEXT_FILTER_DEBOUNCE_MS = 400;

/** Retrasa un valor para no consultar al servidor en cada tecla. */
const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
    const [debounced, setDebounced] = React.useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);

    return debounced;
};

const ProtocolConsumptionSyncProcessLogComponent = (props: ComponentFunctions.Props) => {
    const { t } = useTranslation();
    const styles = useProtocolConsumptionSyncProcessLogStyles();
    // El servidor pagina y filtra: severidad y tipo admiten un solo valor porque
    // así los expone la API, y filtrar en el cliente sólo alcanzaría a la página
    // cargada —los totales y el recorrido de páginas quedarían mal—.
    const [selectedSeverity, setSelectedSeverity] = React.useState<SyncProcessLogSeverityEnum | undefined>(undefined);
    const [selectedSubject, setSelectedSubject] = React.useState<SyncProcessLogSubjectTypeEnum | undefined>(undefined);
    const [expandedLogId, setExpandedLogId] = React.useState<string | null>(null);
    const [searchText, setSearchText] = React.useState<string>('');
    const [patientNumber, setPatientNumber] = React.useState<string>('');
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(SYNC_PROCESS_LOGS_DEFAULT_PAGE_SIZE);

    // Lo que se tipea no dispara una consulta por tecla: se espera a que la
    // escritura se detenga.
    const debouncedSearchText = useDebouncedValue(searchText, TEXT_FILTER_DEBOUNCE_MS);
    const debouncedPatientNumber = useDebouncedValue(patientNumber, TEXT_FILTER_DEBOUNCE_MS);

    const filters: ISyncProcessLogsFilters = useMemo(() => ({
        severity: selectedSeverity,
        subjectType: selectedSubject,
        patientNumber: debouncedPatientNumber.trim() || undefined,
        searchText: debouncedSearchText.trim() || undefined,
    }), [selectedSeverity, selectedSubject, debouncedPatientNumber, debouncedSearchText]);

    // Cambiar un filtro vuelve a la primera página: quedarse en la 7 de un
    // listado que ahora tiene 2 mostraría una página vacía.
    useEffect(() => {
        setPageNumber(1);
    }, [selectedSeverity, selectedSubject, debouncedPatientNumber, debouncedSearchText, pageSize]);

    useEffect(() => {
        if (props.isOpen && props.SyncProcessId) {
            props.GetProtocolConsumptionSyncProcessLogsById(
                props.ProtocolId,
                props.SyncProcessId,
                pageNumber,
                pageSize,
                filters
            );
        }
    }, [props.isOpen, props.ProtocolId, props.SyncProcessId, pageNumber, pageSize, filters]);

    useEffect(() => {
        if (!props.isOpen) {
            setSelectedSeverity(undefined);
            setSelectedSubject(undefined);
            setExpandedLogId(null);
            setSearchText('');
            setPatientNumber('');
            setPageNumber(1);
        }
    }, [props.isOpen]);

    const refreshLogs = () => {
        props.GetProtocolConsumptionSyncProcessLogsById(
            props.ProtocolId,
            props.SyncProcessId,
            pageNumber,
            pageSize,
            filters
        );
    };

    const severityLabel = (severity: SyncProcessLogSeverityEnum) => {
        switch (severity) {
            case SyncProcessLogSeverityEnum.Warning:
                return t('consumptionSyncProcess.logs.severity.warning', 'Warning');
            case SyncProcessLogSeverityEnum.Error:
                return t('consumptionSyncProcess.logs.severity.error', 'Error');
            default:
                return t('consumptionSyncProcess.logs.severity.info', 'Info');
        }
    };

    const severityAppearance = (severity: SyncProcessLogSeverityEnum): 'filled' | 'tint' => {
        return severity === SyncProcessLogSeverityEnum.Info ? 'tint' : 'filled';
    };

    const severityColor = (severity: SyncProcessLogSeverityEnum): 'brand' | 'warning' | 'danger' => {
        switch (severity) {
            case SyncProcessLogSeverityEnum.Warning:
                return 'warning';
            case SyncProcessLogSeverityEnum.Error:
                return 'danger';
            default:
                return 'brand';
        }
    };

    const subjectLabel = (subjectType: SyncProcessLogSubjectTypeEnum) => {
        switch (subjectType) {
            case SyncProcessLogSubjectTypeEnum.Visit:
                return t('consumptionSyncProcess.logs.subject.visit', 'Visit');
            case SyncProcessLogSubjectTypeEnum.Concept:
                return t('consumptionSyncProcess.logs.subject.concept', 'Concept');
            case SyncProcessLogSubjectTypeEnum.Period:
                return t('consumptionSyncProcess.logs.subject.period', 'Period');
            case SyncProcessLogSubjectTypeEnum.Contract:
                return t('consumptionSyncProcess.logs.subject.contract', 'Contract');
            default:
                return t('consumptionSyncProcess.logs.subject.general', 'General');
        }
    };

    const severityOptions: SyncProcessLogSeverityEnum[] = [
        SyncProcessLogSeverityEnum.Info,
        SyncProcessLogSeverityEnum.Warning,
        SyncProcessLogSeverityEnum.Error,
    ];

    const subjectOptions: SyncProcessLogSubjectTypeEnum[] = [
        SyncProcessLogSubjectTypeEnum.General,
        SyncProcessLogSubjectTypeEnum.Visit,
        SyncProcessLogSubjectTypeEnum.Concept,
        SyncProcessLogSubjectTypeEnum.Period,
        SyncProcessLogSubjectTypeEnum.Contract,
    ];

    // Volver a tocar el filtro activo lo quita: es la forma de decir "todas"
    // sin tener que ir hasta el chip de al lado.
    const toggleSeverity = (severity: SyncProcessLogSeverityEnum) => {
        setSelectedSeverity((current) => (current === severity ? undefined : severity));
    };

    const toggleSubject = (subjectType: SyncProcessLogSubjectTypeEnum) => {
        setSelectedSubject((current) => (current === subjectType ? undefined : subjectType));
    };

    // Ya vienen filtrados del servidor: ac\u00e1 s\u00f3lo se muestran.
    const pageLogs = props.logs || [];

    const processLogLevel = getSyncProcessLogLevel(props.MinLogSeverity);

    const counters = useMemo(() => {
        return pageLogs.reduce(
            (accumulator, log) => {
                if (log.severity === SyncProcessLogSeverityEnum.Warning) {
                    accumulator.warning += 1;
                } else if (log.severity === SyncProcessLogSeverityEnum.Error) {
                    accumulator.error += 1;
                } else {
                    accumulator.info += 1;
                }
                return accumulator;
            },
            { info: 0, warning: 0, error: 0 }
        );
    }, [pageLogs]);

    const renderLogItem = (log: ProtocolConsumptionSyncProcessLog) => {
        let accentClass = styles.itemAccentInfo;

        if (log.severity === SyncProcessLogSeverityEnum.Error) {
            accentClass = styles.itemAccentError;
        } else if (log.severity === SyncProcessLogSeverityEnum.Warning) {
            accentClass = styles.itemAccentWarning;
        }

        const title: string = log.subjectName || t('consumptionSyncProcess.logs.labels.noTitle', 'Evento del proceso') || '';
        const preview = log.message || log.suggestion || t('consumptionSyncProcess.logs.labels.noPreview', 'Sin detalle adicional');
        const isExpanded = expandedLogId === log.id;
        const toggleLabel = isExpanded
            ? t('consumptionSyncProcess.logs.actions.collapse', 'Contraer detalle del log')
            : t('consumptionSyncProcess.logs.actions.expand', 'Expandir detalle del log');
        const toggleExpanded = () => {
            setExpandedLogId((current) => current === log.id ? null : log.id);
        };

        const fullDate = moment(log.created).format('DD/MM/YYYY HH:mm:ss');

        return (
            <div
                key={log.id}
                className={`${styles.item} ${isExpanded ? styles.itemExpanded : ''}`}
            >
                <div className={`${styles.itemAccent} ${accentClass}`} aria-hidden />
                <div className={styles.itemMain}>
                    {/* El botón cubre sólo la cabecera. Antes tapaba la fila entera
                        y eso impedía seleccionar el texto del detalle: en un log,
                        copiar el mensaje de error es de lo primero que se hace. */}
                    <div className={styles.itemHeaderZone}>
                        <button
                            type="button"
                            className={styles.itemAction}
                            onClick={toggleExpanded}
                            aria-label={toggleLabel}
                            aria-expanded={isExpanded}
                        >
                            <span className={styles.srOnly}>{toggleLabel}</span>
                        </button>

                        <div className={styles.itemHeader}>
                            {/* Una sola insignia: la severidad es lo que se busca al
                                recorrer. El resto pasa a metadato atenuado, que ocupa
                                menos y deja al título el ancho que necesita. */}
                            <Badge
                                className={styles.itemBadge}
                                appearance={severityAppearance(log.severity)}
                                color={severityColor(log.severity)}
                            >
                                {severityLabel(log.severity)}
                            </Badge>
                            <Text className={styles.itemTitle} title={title}>{title}</Text>
                            <span className={styles.itemInlineMeta}>
                                <span>{subjectLabel(log.subjectType)}</span>
                                {log.patientNumber && (
                                    <>
                                        <span aria-hidden>·</span>
                                        <span>{log.patientNumber}</span>
                                    </>
                                )}
                            </span>
                            {/* La fecha completa queda en el title: en un log casi todo
                                es del mismo día, y repetirla en cada fila tapa la hora,
                                que es lo que de verdad se compara. */}
                            <Text className={styles.itemTime} title={fullDate}>
                                {moment(log.created).format('HH:mm:ss')}
                            </Text>
                            <span
                                className={`${styles.itemChevron} ${isExpanded ? styles.itemChevronOpen : ''}`}
                                aria-hidden
                            >
                                <ChevronRight16Regular />
                            </span>
                        </div>

                        {!isExpanded && (
                            <Text className={styles.itemPreviewText}>
                                {preview}
                            </Text>
                        )}
                    </div>

                    <div className={`${styles.itemExpandedContent} ${isExpanded ? styles.itemExpandedContentOpen : ''}`}>
                        <div className={styles.itemExpandedInner}>
                          <div className={styles.itemExpandedPadding}>
                            {log.message && (
                                <div className={styles.detailSection}>
                                    <Text className={styles.detailLabel}>
                                        {t('consumptionSyncProcess.logs.labels.message', 'Mensaje')}
                                    </Text>
                                    <Text className={styles.itemMessage}>{log.message}</Text>
                                </div>
                            )}

                            {log.suggestion && (
                                <div className={styles.suggestionBox}>
                                    <Text className={styles.suggestionLabel}>
                                        {t('consumptionSyncProcess.logs.labels.suggestion', 'Sugerencia')}
                                    </Text>
                                    <Text className={styles.itemMessage}>{log.suggestion}</Text>
                                </div>
                            )}

                            {/* Sólo lo que la fila plegada no muestra. Antes se
                                repetían acá Tipo, Severidad, Sujeto y Paciente, que
                                ya se leen arriba: abrir no agregaba nada. */}
                            <div className={styles.itemDetailsGrid}>
                                <div className={styles.detailPill}>
                                    <Text className={styles.detailLabel}>{t('consumptionSyncProcess.logs.labels.date', 'Fecha')}</Text>
                                    <Text className={styles.detailValue}>{fullDate}</Text>
                                </div>
                                {log.subjectId && (
                                    <div className={styles.detailPill}>
                                        <Text className={styles.detailLabel}>{t('consumptionSyncProcess.logs.labels.subjectId', 'ID del sujeto')}</Text>
                                        <Text className={styles.detailValue}>{log.subjectId}</Text>
                                    </div>
                                )}
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Dibujar una página entera de eventos es lo más caro de la pantalla, así
    // que se rehace sólo cuando cambian los datos o la fila abierta: sin esto,
    // cualquier re-render del árbol repetía las cien filas.
    const renderedList = useMemo(
        () => (
            <div className={styles.list}>
                {pageLogs.map(renderLogItem)}
            </div>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pageLogs, expandedLogId, styles, t]
    );

    let content = renderedList;

    // El spinner a pantalla completa se reserva para la primera carga. Al
    // cambiar de página o de filtro se mantiene la lista anterior a la vista:
    // vaciarla y volver a llenarla es lo que hace sentir lenta la pantalla.
    const isFirstLoad = props.isLoading && pageLogs.length === 0;

    if (isFirstLoad) {
        content = (
            <div className={styles.centeredState}>
                <Spinner label={t('consumptionSyncProcess.logs.loading', 'Cargando logs del proceso...')} />
            </div>
        );
    } else if (!props.isLoading && pageLogs.length === 0) {
        // Con el filtrado en el servidor, "vacío" y "vacío por los filtros" se
        // distinguen por si hay algún filtro puesto, no por comparar listas.
        const emptyMessage = hasActiveSyncProcessLogsFilters(filters)
            ? t('consumptionSyncProcess.logs.emptyFiltered', 'No hay eventos para los filtros seleccionados.')
            : t('consumptionSyncProcess.logs.empty', 'Este proceso todavía no tiene eventos registrados.');

        content = (
            <div className={styles.emptyState}>
                <Text>{emptyMessage}</Text>
            </div>
        );
    }

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title={t('consumptionSyncProcess.logs.title', 'Log del proceso de sincronización')}
            onDismiss={props.onDismiss}
            closeLabel={t('consumptionSyncProcess.logs.actions.close', 'Cerrar logs')}
            headerActions={
                <Button
                    appearance="subtle"
                    icon={<ArrowClockwise24Regular />}
                    onClick={refreshLogs}
                    aria-label={t('consumptionSyncProcess.logs.actions.refresh', 'Actualizar logs')}
                />
            }
            bodyClassName={styles.drawerBody}
            testId="sync-process-log-drawer"
        >
                <div className={styles.controlsPanel}>
                    {/* Resumen: un solo número grande —el total real del servidor—
                        y el desglose de la página en cuerpo chico, que es lo que
                        vale. El endpoint no expone totales por severidad. */}
                    <div className={styles.summaryStrip}>
                        <div>
                            <div className={styles.summaryTotal}>
                                <Text className={styles.summaryTotalValue}>{props.paginationState.totalRecords}</Text>
                                <Text className={styles.summaryTotalLabel}>
                                    {t('consumptionSyncProcess.logs.summary.total', 'Eventos')}
                                </Text>
                            </div>
                            {hasActiveSyncProcessLogsFilters(filters) && (
                                <Text className={styles.summaryFilteredHint}>
                                    {t('consumptionSyncProcess.logs.summary.filtered', 'con los filtros aplicados')}
                                </Text>
                            )}
                        </div>

                        <div className={styles.breakdown}>
                            {/* Con qué umbral corrió el proceso: sin esto, un log
                                sin eventos Info se lee como "no pasó nada" y no
                                como "no se registró". */}
                            {processLogLevel && (
                                <Text className={styles.breakdownCaption} title={t(processLogLevel.descriptionKey)}>
                                    {t('consumptionSyncProcess.logs.summary.level', {
                                        level: t(processLogLevel.labelKey),
                                    })}
                                </Text>
                            )}
                            <Text className={styles.breakdownCaption}>
                                {t('consumptionSyncProcess.logs.summary.inPage', 'En esta página')}
                            </Text>
                            <div className={styles.breakdownItems}>
                                <span className={styles.breakdownItem}>
                                    <span className={`${styles.dot} ${styles.dotInfo}`} />
                                    <span className={styles.breakdownCount}>{counters.info}</span>
                                    {t('consumptionSyncProcess.logs.severity.info', 'Info')}
                                </span>
                                <span className={styles.breakdownItem}>
                                    <span className={`${styles.dot} ${styles.dotWarning}`} />
                                    <span className={styles.breakdownCount}>{counters.warning}</span>
                                    {t('consumptionSyncProcess.logs.severity.warning', 'Warning')}
                                </span>
                                <span className={styles.breakdownItem}>
                                    <span className={`${styles.dot} ${styles.dotError}`} />
                                    <span className={styles.breakdownCount}>{counters.error}</span>
                                    {t('consumptionSyncProcess.logs.severity.error', 'Error')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.divider} />

                    <div className={styles.filtersBlock}>
                        <div className={styles.filtersInputs}>
                        <div className={styles.filterField}>
                            <Text className={styles.filterLabel}>{t('consumptionSyncProcess.logs.filters.search', 'Buscar')}</Text>
                            <Input
                                contentBefore={<Search20Regular />}
                                contentAfter={
                                    searchText
                                        ? <Button
                                            appearance='transparent'
                                            size='small'
                                            icon={<Dismiss20Regular />}
                                            onClick={() => setSearchText('')}
                                            aria-label={t('consumptionSyncProcess.logs.filters.clearSearch', 'Limpiar búsqueda')}
                                          />
                                        : undefined
                                }
                                placeholder={t('consumptionSyncProcess.logs.filters.searchPlaceholder', 'Buscar en mensajes, paciente, sujeto...')}
                                value={searchText}
                                onChange={(_, data) => setSearchText(data.value)}
                                style={{ width: '100%' }}
                                aria-label={t('consumptionSyncProcess.logs.filters.searchLabel', 'Buscar en el log')}
                            />
                        </div>

                        {/* El paciente tiene su propio filtro porque la búsqueda de
                            texto del servidor sólo mira mensaje, sujeto y sugerencia. */}
                        <div className={styles.filterField}>
                            <Text className={styles.filterLabel}>{t('consumptionSyncProcess.logs.filters.patient', 'Paciente')}</Text>
                            <Input
                                contentAfter={
                                    patientNumber
                                        ? <Button
                                            appearance='transparent'
                                            size='small'
                                            icon={<Dismiss20Regular />}
                                            onClick={() => setPatientNumber('')}
                                            aria-label={t('consumptionSyncProcess.logs.filters.clearPatient', 'Limpiar filtro de paciente')}
                                          />
                                        : undefined
                                }
                                placeholder={t('consumptionSyncProcess.logs.filters.patientPlaceholder', 'Número de paciente en el protocolo')}
                                value={patientNumber}
                                onChange={(_, data) => setPatientNumber(data.value)}
                                style={{ width: '100%' }}
                                aria-label={t('consumptionSyncProcess.logs.filters.patientLabel', 'Filtrar por paciente')}
                            />
                        </div>

                        </div>

                        <div className={styles.filterGroup}>
                            <Text className={styles.filterGroupLabel}>{t('consumptionSyncProcess.logs.filters.severity', 'Severidad')}</Text>
                            <div className={styles.filterChips}>
                                <Button
                                    appearance={selectedSeverity === undefined ? 'primary' : 'secondary'}
                                    className={styles.filterChip}
                                    onClick={() => setSelectedSeverity(undefined)}
                                >
                                    {t('consumptionSyncProcess.logs.filters.all', 'Todas')}
                                </Button>
                                {severityOptions.map((severity) => {
                                    // Por debajo del umbral de la corrida no hay nada
                                    // registrado: el chip se deshabilita en vez de
                                    // devolver un listado vacío sin explicación.
                                    const notRecorded = isSeverityBelowThreshold(severity, props.MinLogSeverity);
                                    return (
                                        <Button
                                            key={severity}
                                            appearance={selectedSeverity === severity ? 'primary' : 'secondary'}
                                            className={styles.filterChip}
                                            disabled={notRecorded}
                                            title={notRecorded
                                                ? t('consumptionSyncProcess.logs.filters.notRecorded', 'Esta corrida no registró eventos de este nivel')
                                                : undefined}
                                            onClick={() => toggleSeverity(severity)}
                                        >
                                            {severityLabel(severity)}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={styles.filterGroup}>
                            <Text className={styles.filterGroupLabel}>{t('consumptionSyncProcess.logs.filters.subject', 'Tipo')}</Text>
                            <div className={styles.filterChips}>
                                <Button
                                    appearance={selectedSubject === undefined ? 'primary' : 'secondary'}
                                    className={styles.filterChip}
                                    onClick={() => setSelectedSubject(undefined)}
                                >
                                    {t('consumptionSyncProcess.logs.filters.all', 'Todas')}
                                </Button>
                                {subjectOptions.map((subjectType) => (
                                    <Button
                                        key={subjectType}
                                        appearance={selectedSubject === subjectType ? 'primary' : 'secondary'}
                                        className={styles.filterChip}
                                        onClick={() => toggleSubject(subjectType)}
                                    >
                                        {subjectLabel(subjectType)}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {hasActiveSyncProcessLogsFilters(filters) && (
                            <div className={styles.filterActions}>
                                <Button appearance="subtle" onClick={() => {
                                    setSelectedSeverity(undefined);
                                    setSelectedSubject(undefined);
                                    setSearchText('');
                                    setPatientNumber('');
                                }}>
                                    {t('consumptionSyncProcess.logs.filters.clear', 'Limpiar filtros')}
                                </Button>
                            </div>
                        )}
                    </div>

                </div>

                {/* Fuera de la tarjeta de controles: un error de carga no es un
                    control, y ahí adentro se leía como parte de los filtros. */}
                {props.failureLoading && (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>{t('consumptionSyncProcess.logs.error.title', 'No se pudieron obtener los logs')}</MessageBarTitle>
                            {props.errorGetting?.ErrorMessage}
                        </MessageBarBody>
                    </MessageBar>
                )}

                {/* Mientras se recarga, la lista se atenúa en vez de desaparecer:
                    se ve que algo está pasando sin perder el contexto. */}
                <div className={`${styles.scrollSection} ${props.isLoading && !isFirstLoad ? styles.scrollSectionBusy : ''}`}>
                    {content}
                </div>

                {/* Sólo cuando hay más de una página: en un proceso con pocos
                    eventos el control sería ruido sin función. */}
                {props.paginationState.totalPages > 1 && (
                    <div className={styles.paginationSection}>
                        <PaginationControlComponent
                            paginationState={props.paginationState}
                            showedRecords={pageLogs.length}
                            isLoading={props.isLoading}
                            setPageNumber={setPageNumber}
                            setPageSize={setPageSize}
                        />
                    </div>
                )}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps,
)(ProtocolConsumptionSyncProcessLogComponent as any);